import { Entity } from '@backstage/catalog-model';

/** Annotation key for Dynatrace service ID */
export const DYNATRACE_SERVICE_ANNOTATION =
  'hub.emis-x.uk/observability-service';

/** Annotation key for Dynatrace web application ID */
export const DYNATRACE_APPLICATION_ANNOTATION =
  'hub.emis-x.uk/observability-application';

/** Annotation key for the Dynatrace Kubernetes cluster name (k8s.cluster.name) */
export const DYNATRACE_CLUSTER_ANNOTATION =
  'hub.emis-x.uk/observability-cluster';

/**
 * Time range options for Dynatrace queries.
 * @public
 */
export type TimeRange =
  | '30m'
  | '1h'
  | '2h'
  | '6h'
  | '12h'
  | 'today'
  | 'yesterday'
  | '24h'
  | '3d'
  | '7d';

/**
 * Time range option with display label.
 * @public
 */
export interface TimeRangeOption {
  value: TimeRange;
  label: string;
}

/**
 * Available time range options for the UI selector.
 * @public
 */
export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { value: '30m', label: 'Last 30 mins' },
  { value: '1h', label: 'Last 1 hour' },
  { value: '2h', label: 'Last 2 hours' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '12h', label: 'Last 12 hours' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '3d', label: 'Last 3 days' },
  { value: '7d', label: 'Last 7 days' },
];

/**
 * Default time range for observability queries.
 * @public
 */
export const DEFAULT_TIME_RANGE: TimeRange = '7d';

export interface ClusterHistoryPoint {
  timestamp: string;
  nodesReady: number | null;
  podsRunning: number | null;
  podsDesired: number | null;
  podsPending: number | null;
  restarts: number | null;
}

export interface ClusterHealthResponse {
  clusterId: string | null;
  nodes: { ready: number; notReady: number };
  pods: { desired: number; running: number; pending: number; failed: number };
  restarts: number;
  openProblems: number;
  history: ClusterHistoryPoint[];
  topRestarts: { namespace: string; workload: string; restarts: number }[];
  degradedWorkloads: {
    namespace: string;
    workload: string;
    desired: number;
    running: number;
  }[];
}

export interface AlertCountResponse {
  count: number;
  hasAlerts: boolean;
}

/**
 * Common props for metric cards.
 * @public
 */
export interface MetricCardProps {
  entity: Entity;
  title?: string;
  timeRange: TimeRange;
}

/**
 * Base interface for timeseries data points.
 * @public
 */
export interface BaseDataPoint {
  timestamp: string;
}

/**
 * Data point for response time percentiles.
 * @public
 */
export interface ResponseTimeDataPoint extends BaseDataPoint {
  p50?: number | null;
  p90?: number | null;
  p99?: number | null;
}

/**
 * Data point for throughput metrics.
 * @public
 */
export interface ThroughputDataPoint extends BaseDataPoint {
  successfulRequests?: number | null;
  failureCount?: number | null;
}

/**
 * Data point for failure rate metrics.
 * @public
 */
export interface FailureRateDataPoint extends BaseDataPoint {
  failureRate?: number | null;
}

/**
 * Data point for HTTP error metrics.
 * @public
 */
export interface HttpErrorsDataPoint extends BaseDataPoint {
  failureCount4xx?: number | null;
  failureCount5xx?: number | null;
}

/**
 * Web Vitals p75 data point. CLS is normalized to its standard 0-1 scale.
 */
export interface WebVitalsDataPoint extends BaseDataPoint {
  lcp: number | null;
  inp: number | null;
  fid: number | null;
  cls: number | null;
}

export interface ApplicationOverviewDataPoint extends BaseDataPoint {
  activeUsers?: number | null;
  activeSessions?: number | null;
  availability?: number | null;
}

/**
 * API endpoint paths for the backend.
 * @public
 */
export const API_ENDPOINTS = {
  CONFIG: '/config',
  EXECUTE_QUERY: '/execute-query',
  THROUGHPUT: '/throughput',
  FAILURE_RATE: '/failure-rate',
  HTTP_ERRORS: '/http-errors',
  WEB_VITALS: '/web-vitals',
  APPLICATION_FRONTEND: '/application-frontend',
  APPLICATION_USAGE: '/application-usage',
  APPLICATION_AVAILABILITY: '/application-availability',
  VALIDATE_SERVICE: '/validate-service',
  ALERTS: '/alerts',
  APPLICATION_ALERTS: '/application-alerts',
  CLUSTER_HEALTH: '/cluster-health',
} as const;
